import { useState } from "react";
import { Link as RouterLink, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Autocomplete, Badge, Box, Button, Collapse, Container, Dialog, Divider, Drawer, IconButton, List, ListItemButton,
  ListItemText, Menu, MenuItem, TextField, Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { BRAND, FOOTER_COLS, LEGAL_LINKS, NAV, NAV_ACTIONS, PROMO, SEARCH_INDEX, SOCIAL } from "../brand";
import { palette5, tokens } from "../theme";
import { useAuth0 } from "../hooks/useAuth0";
import { useCart } from "./CartContext";
import EmailCapture from "./EmailCapture";

function Logo({ light = true, size = 21 }) {
  return (
    <Box component={RouterLink} to="/" aria-label="VEYSO home" sx={{ textDecoration: "none", color: light ? "#fff" : tokens.ink, display: "flex", alignItems: "center" }}>
      <Typography sx={{ fontWeight: 400, fontSize: size, letterSpacing: ".18em", pl: 0.5 }}>VEYSO</Typography>
    </Box>
  );
}

function NavItem({ item }) {
  const [anchor, setAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
  const sx = {
    color: "#fff", fontSize: 16, fontWeight: 400, px: 1.9, py: 0.8, borderRadius: 999, cursor: "pointer", opacity: active ? 1 : 0.92,
    textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 0.3, background: active ? "rgba(255,255,255,.12)" : "transparent", border: 0, fontFamily: "inherit",
    "&:hover": { background: "rgba(255,255,255,.14)" },
  };
  if (!item.children) {
    return <Box component={NavLink} to={item.to} sx={sx}>{item.label}</Box>;
  }
  return (
    <>
      <Box component="button" type="button" sx={sx} onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="true" aria-expanded={Boolean(anchor)}>
        {item.label}<KeyboardArrowDownIcon sx={{ fontSize: 18, opacity: 0.8 }} />
      </Box>
      <Menu
        anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { mt: 1.5, borderRadius: "18px", minWidth: 240, background: "rgba(250,247,242,.97)", backdropFilter: "blur(10px)", border: `1px solid ${tokens.line}` } } }}
      >
        <MenuItem onClick={() => { setAnchor(null); navigate(item.to); }} sx={{ fontSize: 15, py: 1, fontWeight: 500 }}>{item.label} overview</MenuItem>
        {item.children.map((c) => (
          <MenuItem key={c.to + c.label} onClick={() => { setAnchor(null); navigate(c.to); }} sx={{ fontSize: 15, py: 1 }}>{c.label}</MenuItem>
        ))}
      </Menu>
    </>
  );
}

function SearchDialog({ open, onClose }) {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "22px", p: 2, mt: -20 } }}>
      <Autocomplete
        autoHighlight openOnFocus options={SEARCH_INDEX} getOptionLabel={(o) => o.label}
        onChange={(_, v) => { if (v) { onClose(); navigate(v.to); } }}
        renderInput={(params) => <TextField {...params} autoFocus placeholder="Search VEYSO" />}
      />
    </Dialog>
  );
}

const barBtn = {
  outline: { whiteSpace: "nowrap", color: "#fff", border: "1px solid rgba(255,255,255,.8)", px: 2.5, py: 1, fontSize: 15, "&:hover": { background: "rgba(255,255,255,.16)", borderColor: "#fff" } },
  solid: { whiteSpace: "nowrap", background: tokens.oxide, color: "#fff", px: 2.75, py: 1.1, fontSize: 15, "&:hover": { background: tokens.oxideHover } },
};

const PROMO_H = 40;

function Header() {
  const [drawer, setDrawer] = useState(false);
  const [open, setOpen] = useState({});
  const [search, setSearch] = useState(false);
  const [anchor, setAnchor] = useState(null); // account menu (signed-in, desktop)
  const { isAuthenticated, logout, user } = useAuth0();
  const isAdmin = (user?.roles || []).includes("Admin"); // admin-only menu links
  const { count } = useCart();
  const navigate = useNavigate();
  const account = isAuthenticated ? NAV_ACTIONS.account : NAV_ACTIONS.login;

  return (
    <>
      {BRAND.showPreviewBar && (
        <Box sx={{ background: tokens.forestDark, color: palette5.basalt[4], fontSize: 12.5, textAlign: "center", py: 0.6 }}>
          Preview build: sample content and sample data
        </Box>
      )}
      {/* Promo banner: stays pinned at the top while the page scrolls. */}
      <Box role="region" aria-label="Promotion" sx={{ position: "sticky", top: 0, zIndex: 25, height: PROMO_H, background: tokens.brown, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, px: 2 }}>
        <Typography sx={{ fontSize: { xs: 13.5, sm: 14.5 }, letterSpacing: ".02em" }}>
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{PROMO.text}</Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{PROMO.short}</Box>
        </Typography>
        <Button component={RouterLink} to={PROMO.to} size="small" endIcon={<ArrowOutwardIcon sx={{ fontSize: "14px !important" }} />}
          sx={{ background: tokens.oxide, color: "#fff", px: 1.75, py: 0.35, fontSize: 13, minHeight: 0, "&:hover": { background: tokens.oxideHover } }}>
          {PROMO.cta}
        </Button>
      </Box>
      <Box sx={{ position: "fixed", top: PROMO_H + 12, left: 0, right: 0, zIndex: 20, display: "flex", justifyContent: "center", px: { xs: 2.5, sm: 6, md: 8 }, pointerEvents: "none" }}>
        <Box
          component="nav" aria-label="Main"
          sx={{
            pointerEvents: "auto", display: "flex", alignItems: "center", gap: { xs: 1, md: 2 }, width: "100%", maxWidth: 1312,
            pl: { xs: 2, md: 3.5 }, pr: { xs: 1, md: 1.25 }, py: { xs: 1, md: 1.1 }, borderRadius: { xs: "16px", md: "14px" }, color: "#fff",
            background: "rgba(64,62,53,.78)", backdropFilter: "blur(18px)", border: "1px solid rgba(255,255,255,.22)",
          }}
        >
          <Logo size={22} />
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.3, ml: { md: 6, lg: 14 } }}>
            {NAV.map((n) => <NavItem key={n.label} item={n} />)}
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton aria-label="Search" onClick={() => setSearch(true)} sx={{ color: "#fff", display: { xs: "none", md: "inline-flex" } }}><SearchIcon /></IconButton>
          <IconButton aria-label="Cart" component={RouterLink} to="/cart" sx={{ color: "#fff" }}>
            <Badge badgeContent={count} color="primary"><ShoppingBagOutlinedIcon /></Badge>
          </IconButton>
          <Button component={RouterLink} to={NAV_ACTIONS.clinicians.to} endIcon={<ArrowOutwardIcon sx={{ fontSize: "16px !important" }} />} sx={{ ...barBtn.outline, display: { xs: "none", sm: "inline-flex" } }}>
            {NAV_ACTIONS.clinicians.label}
          </Button>
          {isAuthenticated ? (
            <>
              <Button onClick={(e) => setAnchor(e.currentTarget)} endIcon={<KeyboardArrowDownIcon />} sx={{ ...barBtn.solid, display: { xs: "none", sm: "inline-flex" } }}>{account.label}</Button>
              <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                <MenuItem onClick={() => { setAnchor(null); navigate("/dashboard"); }}>My results</MenuItem>
                <MenuItem onClick={() => { setAnchor(null); navigate("/visits"); }}>My visits</MenuItem>
                {isAdmin && <Divider />}
                {isAdmin && <MenuItem onClick={() => { setAnchor(null); navigate("/admin/rx"); }}>Rx catalog and orders</MenuItem>}
                <Divider />
                <MenuItem onClick={() => { setAnchor(null); logout(); }}>Log out</MenuItem>
              </Menu>
            </>
          ) : (
            <Button component={RouterLink} to={account.to} sx={{ ...barBtn.solid, display: { xs: "none", sm: "inline-flex" } }}>{account.label}</Button>
          )}
          <IconButton aria-label="Open menu" onClick={() => setDrawer(true)} sx={{ display: { md: "none" }, color: "#fff" }}><MenuIcon /></IconButton>
        </Box>
      </Box>
      <SearchDialog open={search} onClose={() => setSearch(false)} />

      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)} PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, background: tokens.forestDark, color: "#fff" } }}>
        <Box sx={{ p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Logo size={22} />
          <IconButton aria-label="Close menu" onClick={() => setDrawer(false)} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </Box>
        <Divider sx={{ borderColor: "rgba(255,255,255,.15)" }} />
        <List sx={{ px: 1 }}>
          {NAV.map((n) => (
            <Box key={n.label}>
              <ListItemButton onClick={() => (n.children ? setOpen({ ...open, [n.label]: !open[n.label] }) : (setDrawer(false), navigate(n.to)))}>
                <ListItemText primary={n.label} primaryTypographyProps={{ fontSize: 22, fontWeight: 300 }} />
                {n.children && <KeyboardArrowDownIcon sx={{ transform: open[n.label] ? "rotate(180deg)" : "none" }} />}
              </ListItemButton>
              {n.children && (
                <Collapse in={Boolean(open[n.label])}>
                  {n.children.map((c) => (
                    <ListItemButton key={c.to + c.label} sx={{ pl: 4 }} component={RouterLink} to={c.to} onClick={() => setDrawer(false)}>
                      <ListItemText primary={c.label} primaryTypographyProps={{ fontSize: 16, color: "rgba(255,255,255,.72)" }} />
                    </ListItemButton>
                  ))}
                </Collapse>
              )}
            </Box>
          ))}
          <ListItemButton onClick={() => { setDrawer(false); setSearch(true); }}><ListItemText primary="Search" primaryTypographyProps={{ fontSize: 22, fontWeight: 300 }} /></ListItemButton>
        </List>
        <Box sx={{ p: 2.5, mt: "auto", display: "grid", gap: 1.25 }}>
          <Button component={RouterLink} to={NAV_ACTIONS.clinicians.to} onClick={() => setDrawer(false)} endIcon={<ArrowOutwardIcon sx={{ fontSize: "16px !important" }} />} sx={{ ...barBtn.outline, py: 1.4 }}>{NAV_ACTIONS.clinicians.label}</Button>
          <Button component={RouterLink} to={account.to} onClick={() => setDrawer(false)} sx={{ ...barBtn.solid, py: 1.5 }}>{account.label}</Button>
          {isAdmin && <Button component={RouterLink} to="/admin/rx" onClick={() => setDrawer(false)} sx={{ color: "rgba(255,255,255,.8)" }}>Rx catalog and orders</Button>}
          {isAuthenticated && <Button onClick={() => { setDrawer(false); logout(); }} sx={{ color: "rgba(255,255,255,.8)" }}>Log out</Button>}
        </Box>
      </Drawer>
    </>
  );
}

function Footer() {
  const linkSx = { display: "block", color: "rgba(255,255,255,.88)", textDecoration: "none", fontSize: 16, py: 0.9, "&:hover": { color: "#fff" } };
  return (
    <Box component="footer" sx={{ background: tokens.forestDark, color: "#fff", pt: { xs: 8, md: 11 }, pb: 4 }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 8 } }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { md: "flex-end" }, gap: 5 }}>
          <Box>
            <Typography sx={{ fontWeight: 300, fontSize: { xs: 64, md: 96 }, letterSpacing: ".04em", lineHeight: 1 }}>VEYSO</Typography>
            <Typography sx={{ mt: 2.5, fontSize: 13.5, letterSpacing: ".1em", textTransform: "uppercase", lineHeight: 1.7 }}>
              Less noise. Better decisions.<br />Higher standards.
            </Typography>
          </Box>
          <Box sx={{ width: { xs: "100%", md: 380 } }}>
            <Typography sx={{ fontSize: 15.5, mb: 1.5, opacity: 0.9 }}>Stay in the know</Typography>
            <EmailCapture tone="dark" button="Join" icon={false} message="Please add me to the VEYSO list (footer signup)." />
          </Box>
        </Box>
        <Divider sx={{ my: 6, borderColor: "rgba(255,255,255,.18)" }} />
        <Box sx={{ display: "grid", gap: 4, gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" } }}>
          {FOOTER_COLS.map((c) => (
            <Box key={c.title}>
              <Typography sx={{ fontSize: 12.5, letterSpacing: ".14em", textTransform: "uppercase", opacity: 0.65, mb: 1.5 }}>{c.title}</Typography>
              {c.links.map(([label, to]) => (
                <Typography key={label} component={RouterLink} to={to} sx={linkSx}>{label}</Typography>
              ))}
            </Box>
          ))}
        </Box>
        <Box sx={{ mt: 6, display: "flex", gap: 3, flexWrap: "wrap" }}>
          {SOCIAL.map((n) => (
            <Typography key={n} component="a" href="#" onClick={(e) => e.preventDefault()} sx={{ color: "rgba(255,255,255,.82)", textDecoration: "none", fontSize: 14.5, "&:hover": { color: "#fff" } }}>{n}</Typography>
          ))}
        </Box>
        <Typography sx={{ fontSize: 13, opacity: 0.7, maxWidth: 820, mt: 5, lineHeight: 1.6 }}>
          Preview build with sample content. Nothing here is medical advice and no provider is connected in this preview. A licensed provider decides whether any
          test or treatment is appropriate, and some people are directed to in-person care. In an emergency call 911; for a mental health crisis call or text 988 (US).
        </Typography>
        <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,.18)" }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Typography sx={{ fontSize: 13, opacity: 0.7 }}>© {new Date().getFullYear()} {BRAND.name}. All rights reserved. Available in all 50 states.</Typography>
          <Box sx={{ display: "flex", gap: 3 }}>
            {LEGAL_LINKS.map(([label, to]) => (
              <Typography key={label} component={RouterLink} to={to} sx={{ fontSize: 13, color: "rgba(255,255,255,.7)", textDecoration: "none", "&:hover": { color: "#fff" } }}>{label}</Typography>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

/** `hero` pages start with a gradient panel and the menu floats over it; others get top spacing instead. */
export default function Layout({ children, hero = true }) {
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: tokens.cream, position: "relative" }}>
      <Header />
      <Box component="main" sx={{ flex: 1, pt: hero ? 0 : { xs: 14, md: 16 } }}>{children}</Box>
      <Footer />
    </Box>
  );
}
